import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UTCDeliveryPeriod } from '@epicuro-next/ataraxia/models';
import { dynamicElementsBestPerformingArticlesFiltersKey } from '@epicuro-next/constants/dynamic-elements';
import { IFilterValue } from '@epicuro-next/data/reporting-api';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import {
  formatUTCRange,
  isCurrentDay,
  Placement,
} from '@epicuro-next/utilities';
import { BehaviorSubject } from 'rxjs';

import { DateFilterOptionLabel, dateFilterOptions } from '../../data';
import { deductDaysFilter } from '../../utils';

export interface SelectFilterConfig {
  values: IFilterValue[];
  selected?: string[];
  defaultText: string;
  showAllText?: string;
  isSingle?: boolean;
}

export interface FiltersChangedEvent {
  dateRange?: Required<UTCDeliveryPeriod>;
  brandOrgIds?: string[];
  categories?: string[];
  orderType?: string[];
}

export interface DateFilterConfig {
  minDate: Date;
  maxDate: Date;
  preselected?:
    | DateFilterOptionLabel
    | DateFilterOptionLabel[]
    | { from: string; to: string };
}

@DynamicComponentRegister(dynamicElementsBestPerformingArticlesFiltersKey)
@Component({
  selector: 'epc-best-performing-articles-filters',
  templateUrl: './best-performing-articles-filters.component.html',
  styleUrls: ['./best-performing-articles-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BestPerformingArticlesFiltersComponent
  implements OnInit, OnChanges
{
  public readonly placement = Placement;
  public dateFilterOptions = dateFilterOptions;

  @Input() public brandOrgsFilterConfig?: SelectFilterConfig;
  @Input() public categoriesFilterConfig?: SelectFilterConfig;
  @Input() public orderTypeFilterConfig?: SelectFilterConfig;
  @Input() public dateFilterConfig?: DateFilterConfig;
  @Input() public includeAllFilterValuesInFiltersChangedEvent = false;

  @Output() public filtersChanged = new EventEmitter<FiltersChangedEvent>();

  private lastEmittedFilters: FiltersChangedEvent = {};

  public readonly selectedDateOptionLabel$ = new BehaviorSubject<string>(
    this.dateFilterOptions[0].label,
  );

  public ngOnInit(): void {
    this.presetDateFilter();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const dateFilterConfgKey: keyof this = 'dateFilterConfig';
    const dateFilterConfigChanges = changes[dateFilterConfgKey as string];
    if (dateFilterConfigChanges) {
      this.presetDateFilter();
    }
  }

  public filterChanged(
    event: FiltersChangedEvent,
    config?: SelectFilterConfig,
  ): void {
    const values = Object.values(event);
    if (config !== undefined && values.length === 1) {
      if (config.selected === undefined) {
        config.selected = [];
      } else {
        config.selected.length = 0;
      }
      config.selected.push(...values[0]);
    }
    const filtersToEmit = this.includeAllFilterValuesInFiltersChangedEvent
      ? { ...this.lastEmittedFilters, ...event }
      : event;
    this.filtersChanged.next(filtersToEmit);
    this.lastEmittedFilters = filtersToEmit;
  }

  public filterByDate(value: DateFilterOptionLabel) {
    const dateRange = this.getRangeAndSetDateOptionLabel(value);
    this.filterChanged({ dateRange });
  }

  public customDateRangeSelected(dateRange: Required<UTCDeliveryPeriod>) {
    this.setCustomDateRangeSelected(dateRange);
    this.filterChanged({ dateRange });
  }

  private setCustomDateRangeSelected(dateRange: Required<UTCDeliveryPeriod>) {
    const rangeText = formatUTCRange(`${dateRange.from}/${dateRange.to}`);
    this.setDateOptionLabel(rangeText);
  }

  private presetDateFilter(): void {
    const preselectedDateRangeFilter = this.dateFilterConfig?.preselected;
    if (preselectedDateRangeFilter !== undefined) {
      if (Array.isArray(preselectedDateRangeFilter)) {
        throw new Error('Preselected date by range is not implemented');
      } else {
        if (this.isToFrom(preselectedDateRangeFilter)) {
          this.setDateRangeBasedOnToAndFrom(preselectedDateRangeFilter);
        } else {
          this.getRangeAndSetDateOptionLabel(preselectedDateRangeFilter);
        }
      }
    }
  }

  private setDateRangeBasedOnToAndFrom(preselectedDateRangeFilter: {
    from: string;
    to: string;
  }) {
    const from = new Date(preselectedDateRangeFilter.from);
    const to = new Date(preselectedDateRangeFilter.to);

    let isSet = false;
    if (isCurrentDay(to)) {
      const defaultValue = this.getDefaultValue(from, to);

      if (defaultValue !== undefined) {
        this.getRangeAndSetDateOptionLabel(defaultValue);
        isSet = true;
      }
    }

    if (!isSet) {
      this.setCustomDateRangeSelected(preselectedDateRangeFilter);
    }
  }

  private getDefaultValue(from: Date, to: Date) {
    const daysInBetween = Math.floor(
      Math.abs((from.getTime() - to.getTime()) / (1 * 24 * 60 * 60 * 1000)),
    );

    const defaultValue = dateFilterOptions.find(
      (v) => v.id.toLowerCase() === `last${daysInBetween}days`,
    );
    return defaultValue;
  }

  private isToFrom(
    dateRange: unknown,
  ): dateRange is { from: string; to: string } {
    return (
      dateRange !== undefined &&
      dateRange !== null &&
      typeof dateRange === 'object' &&
      (dateRange as { from?: string }).from !== undefined &&
      (dateRange as { to?: string }).to !== undefined
    );
  }

  private setDateOptionLabel(label: string): void {
    this.selectedDateOptionLabel$.next(label);
  }

  private getRangeAndSetDateOptionLabel({ id, label }: DateFilterOptionLabel): {
    from: string;
    to: string;
  } {
    const dateRange = deductDaysFilter(id);
    this.setDateOptionLabel(label);
    return dateRange;
  }
}
